import { ApiError } from '@clockwise/shared';
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { getSession } from './auth-client';
import { type Me, meQueryOptions } from './me';
import { queryKeys } from './query-keys';

// Cache the BetterAuth session in TanStack Query so beforeLoad reuses it
// instead of refetching on every in-app navigation. 60s is short enough that
// a stale/expired session triggers a redirect within a minute, long enough to
// avoid hammering /api/auth/get-session on every click.
const SESSION_STALE_MS = 60_000;

/** Session query result — shape returned by BetterAuth `getSession()`. */
type SessionResult = Awaited<ReturnType<typeof getSession>>;

/**
 * Load (and cache) the BetterAuth session for a route `beforeLoad`. Shared by
 * `/` and `/_authenticated` so both read the same cache entry with the same
 * freshness. A network failure here rejects out of `beforeLoad` and is handled
 * by the router's error boundary (retryable) — we do NOT assume "logged out".
 */
export function ensureSession(queryClient: QueryClient): Promise<SessionResult> {
  return queryClient.ensureQueryData({
    queryKey: queryKeys.session,
    queryFn: () => getSession(),
    staleTime: SESSION_STALE_MS,
  });
}

/**
 * Ensure the caller resolves to a usable domain identity, returning the `me`
 * payload — or throwing the RIGHT redirect:
 *
 *   - `me` 401s AND the session is genuinely gone → `/login` (re-auth).
 *   - `me` 401s but the session is still valid → the account is unprovisioned
 *     or blocked (inactive / terminated / not-yet-started) → `/no-access`.
 *   - any other error (5xx, timeout, network) is RETHROWN so the router error
 *     boundary can offer a retry. `meQueryOptions` sets `retry: false`, so
 *     without this a single transient blip would masquerade as "no access" and
 *     dead-end a perfectly valid user on the terminal screen.
 *
 * The freshness re-check (`getSession()` uncached) is what tells an expired
 * session apart from a blocked one — the cached session can still read valid
 * for up to `SESSION_STALE_MS` after the server-side session actually expired.
 */
export async function ensureProvisionedMe(queryClient: QueryClient, nextHref: string): Promise<Me> {
  try {
    return await queryClient.ensureQueryData(meQueryOptions);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'UNAUTHENTICATED') {
      const fresh = await getSession().catch(() => null);
      if (!fresh?.data) {
        throw redirect({ to: '/login', search: { next: nextHref } });
      }
      throw redirect({ to: '/no-access' });
    }
    throw err;
  }
}
